/**
 * Helpers for the existing Django-Group-backed Roles multi-select
 * (employer:admin / employer:manager / employer:viewer).
 *
 * T&A roles live in their own dedicated subsystem (TARole rows) — see
 * ``./taRoleScope.ts`` for that. These helpers do NOT touch T&A roles.
 */

export interface RoleLike {
  id: number;
  name: string;
  namespace?: string | null;
}

export const NAMESPACE_LABELS: Record<string, string> = {
  employer: 'Employer',
};

/** Return the role's namespace (``{ns}:{role_name}``), falling back to 'other'. */
export const getNamespace = (role: RoleLike): string => {
  if (role.namespace) return role.namespace;
  if (role.name?.includes(':')) return role.name.split(':')[0];
  return 'other';
};

/** Pretty label for a namespace, e.g. 'employer' -> 'Employer'. */
export const prettyNamespace = (ns: string): string => {
  if (NAMESPACE_LABELS[ns]) return NAMESPACE_LABELS[ns];
  if (ns === 'other') return 'Other';
  return ns;
};

/** Build react-select grouped options from a flat role list.
 *
 * Output is deterministically ordered: namespace groups are sorted by their
 * rendered label, and options within each group are sorted by label, so UI
 * reloads don't reshuffle the dropdown.
 */
export const buildGroupedOptions = (
  availableRoles: RoleLike[],
): Array<{ label: string; options: Array<{ value: number; label: string }> }> => {
  const groups: Record<string, Array<{ value: number; label: string }>> = {};
  availableRoles.forEach((role) => {
    const ns = getNamespace(role);
    if (!groups[ns]) groups[ns] = [];
    groups[ns].push({ value: role.id, label: role.name });
  });
  return Object.entries(groups)
    .map(([ns, options]) => ({
      label: prettyNamespace(ns),
      options: [...options].sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
